/**
 * Weather layer definitions for MapLibre GL JS.
 * Mirrors the React Native weather-layer.ts but uses server-side tile endpoints.
 */

import type { LayerSpecification } from 'maplibre-gl';

export const WEATHER_SOURCE_IDS = {
  nexrad: 'nexrad-raster',
  sigmets: 'sigmets-geojson',
  airmets: 'airmets-geojson',
  pireps: 'pireps-geojson',
} as const;

export const WEATHER_LAYER_IDS = {
  nexrad: 'nexrad-radar',
  sigmets: 'sigmets-fill',
  airmets: 'airmets-fill',
  pireps: 'pireps-circle',
} as const;

// ─── NEXRAD Color Scale (NWS standard) ──────────────────────────────────────

export const NEXRAD_COLOR_SCALE: { dbz: number; color: string; label: string }[] = [
  { dbz: 5,   color: '#00E0E0', label: 'Light' },
  { dbz: 10,  color: '#00C0C0', label: 'Light' },
  { dbz: 15,  color: '#009000', label: 'Light' },
  { dbz: 20,  color: '#00B000', label: 'Moderate' },
  { dbz: 25,  color: '#00D000', label: 'Moderate' },
  { dbz: 30,  color: '#00F000', label: 'Moderate' },
  { dbz: 35,  color: '#E0E000', label: 'Heavy' },
  { dbz: 40,  color: '#E0C000', label: 'Heavy' },
  { dbz: 45,  color: '#E09000', label: 'Very Heavy' },
  { dbz: 50,  color: '#E06000', label: 'Intense' },
  { dbz: 55,  color: '#E00000', label: 'Intense' },
  { dbz: 60,  color: '#C00000', label: 'Extreme' },
  { dbz: 65,  color: '#800080', label: 'Extreme' },
  { dbz: 100, color: '#FF00FF', label: 'Extreme' },
];

/** NEXRAD raster layer */
export function createNexradLayer(sourceId: string, opacity = 0.6): LayerSpecification {
  return {
    id: WEATHER_LAYER_IDS.nexrad,
    type: 'raster',
    source: sourceId,
    layout: { visibility: 'none' },
    paint: { 'raster-opacity': opacity },
  } as LayerSpecification;
}

// ─── SIGMET / AIRMET polygon layers ──────────────────────────────────────────

export type HazardType = 'convective' | 'icing' | 'turbulence' | 'ifr' | 'mountain-obscuration' | 'volcanic-ash' | 'other';

const HAZARD_COLORS: Record<HazardType, string> = {
  convective:            '#EF4444',
  icing:                 '#3B82F6',
  turbulence:            '#F97316',
  ifr:                   '#A78BFA',
  'mountain-obscuration': '#64748B',
  'volcanic-ash':        '#6B7280',
  other:                 '#EAB308',
};

export function getHazardColor(type: HazardType): string {
  return HAZARD_COLORS[type] ?? HAZARD_COLORS.other;
}

export function createSigmetLayer(sourceId: string): LayerSpecification {
  return {
    id: WEATHER_LAYER_IDS.sigmets,
    type: 'fill',
    source: sourceId,
    layout: { visibility: 'none' },
    paint: {
      'fill-color': ['match', ['get', 'hazard'],
        'CONVECTIVE', HAZARD_COLORS.convective,
        'ICE',        HAZARD_COLORS.icing,
        'TURB',       HAZARD_COLORS.turbulence,
        'VA',         HAZARD_COLORS['volcanic-ash'],
        HAZARD_COLORS.other,
      ],
      'fill-opacity': 0.2,
    },
  } as LayerSpecification;
}

export function createAirmetLayer(sourceId: string): LayerSpecification {
  return {
    id: WEATHER_LAYER_IDS.airmets,
    type: 'fill',
    source: sourceId,
    layout: { visibility: 'none' },
    paint: {
      'fill-color': ['match', ['get', 'hazard'],
        'ICE',      HAZARD_COLORS.icing,
        'TURB-HI',  HAZARD_COLORS.turbulence,
        'TURB-LO',  HAZARD_COLORS.turbulence,
        'IFR',      HAZARD_COLORS.ifr,
        'MT_OBSC',  HAZARD_COLORS['mountain-obscuration'],
        HAZARD_COLORS.other,
      ],
      'fill-opacity': 0.15,
    },
  } as LayerSpecification;
}

export function createPirepLayer(sourceId: string): LayerSpecification {
  return {
    id: WEATHER_LAYER_IDS.pireps,
    type: 'circle',
    source: sourceId,
    layout: { visibility: 'none' },
    paint: {
      'circle-radius': 5,
      'circle-color': '#FACC15',
      'circle-stroke-color': '#713F12',
      'circle-stroke-width': 1,
      'circle-opacity': 0.85,
    },
  } as LayerSpecification;
}

// ─── OpenWeatherMap tile layers ──────────────────────────────────────────────

export type OwmLayer = 'precipitation' | 'wind' | 'temperature' | 'clouds' | 'pressure';

const OWM_TILE_URLS: Record<OwmLayer, string> = {
  precipitation: 'https://tile.openweathermap.org/map/precipitation_new/{z}/{x}/{y}.png?appid={apiKey}',
  wind:          'https://tile.openweathermap.org/map/wind_new/{z}/{x}/{y}.png?appid={apiKey}',
  temperature:   'https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid={apiKey}',
  clouds:        'https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid={apiKey}',
  pressure:      'https://tile.openweathermap.org/map/pressure_new/{z}/{x}/{y}.png?appid={apiKey}',
};

export function getOwmTileUrl(layer: OwmLayer, apiKey: string): string {
  return OWM_TILE_URLS[layer].replace('{apiKey}', apiKey);
}
